import { Grid, Typography, styled } from "@mui/material";
import { useRef, useEffect, useState, useMemo } from "react";
import { SpinnerProgress } from "../SpinnerProgress/SpinnerProgress";
import { NonFlyingAssignmentForm } from "./PairingEditorWindow/Forms/NonFlyingAssignmentForm";
import { getNFA } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { ICrewsNonFlyingAssignment, IPosition, IUser } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { formatCrewList } from "src/components/Admin/Tabs/Crew/ManageCrew";
import { DateDisplay } from "src/components/DateDisplay";
import { ICrewNonFlyingAssignmentMap } from "src/redux/reducers/CrewScheduleReducer";
interface INFAEditorWindowProps {
  id: string;
  path: string[];
  windowProps: {
    nfaId: string;
  };
}

// New Non Flying Assignment default values.
const initialValues: ICrewsNonFlyingAssignment = {
  _id: undefined,
  start: undefined,
  end: undefined,
  locationICAO: "",
  locationTimezoneId: "",
  locationCountry: undefined,
  assignmentType: undefined,
  crew: [],
  createdAt: undefined,
  createdBy: undefined,
  foreignRef: undefined,
};

const OCNFAEditorWindow = styled("div")(({ theme }) => ({
  backgroundColor: theme.custom.timeline.contextListBackground,
  "& .form_grid": {
    color: theme.palette.primary.contrastText,
    padding: "10px",
    "& .form_actions": {
      width: "270px",
      margin: "5px 0px 5px 90px !important",
      display: "inline-flex",
      alignItems: "center",
      flexDirection: "row",
      "& .MuiButton-root": {
        width: "90px",
        height: "30px",
        margin: "0 2px",
        padding: "2px",
        fontSize: "11px",
        lineHeight: "12px",
      },
    },
  },
}));

/**
 * A component to edit a NFA
 * @param props - nafid
 */

export const NFAEditorWindow = (props: INFAEditorWindowProps): JSX.Element => {
  const { windowProps } = props;
  const { nfaId } = windowProps;
  const dispatch = useDispatch();

  const selectedCrewDays = useSelector((state: IRootState) => state.crewSchedule.selectedCrewDays);
  const containerRef = useRef<HTMLDivElement>(null);
  const [nfa, setNFA] = useState<ICrewsNonFlyingAssignment | null>(initialValues);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const getNFAAction = async (): Promise<void> => {
      setLoading(true);
      const response = await getNFA(nfaId);
      if (response?._id) {
        setNFA(response);
      }
      setLoading(false);
    };
    if (nfaId) getNFAAction();
  }, [nfaId]);

  const crewNFAs: ICrewNonFlyingAssignmentMap = useSelector(
    (state: IRootState) => state.crewSchedule.crewNonFlyingAssignments
  );

  // If form is used outside of the pairing editor, allow for editing the assigned crew.
  const positions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions).sort(
    (a, b) => (b.displayOrder > a.displayOrder ? -1 : 1)
  );

  const [users, setUsers] = useState<IUser[]>([]);
  const [groupedCrewList, setGroupedCrewList] = useState<any>([]);

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      setLoading(true);
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
      if (Array.isArray(response)) {
        setUsers(response);
      }
      setLoading(false);
    };
    getUsersAction();
  }, []);

  // If positions are users are updated, rebuilt crew list.
  useEffect(() => {
    setGroupedCrewList(formatCrewList(users, positions));
  }, [positions, users]);

  //to make sure that the nfa hasn't been converted to planned or deleted since you opened the editor
  const nfaExistsInRedux = useMemo(() => {
    return !!crewNFAs[nfa?.crew?.[0]?._id]?.nonFlyingAssignments?.find((nonFlying) => nonFlying._id === nfa?._id);
  }, [crewNFAs, nfa]);

  const handleSubmit = async (nonFlyingAssignment: ICrewsNonFlyingAssignment): Promise<void> => {
    const mode = nonFlyingAssignment?._id ? "update" : "create";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Adding"} NFA on selected crew.`,
      successMessage: `NFA successful ${mode === "update" ? "Updated" : "Added"}`,
      errorMessage: `Error: Failed to ${mode === "update" ? "Update" : "Add"} NFA`,
    };
    if (mode === "update") {
      // UPDATE EXISTING
      const response = await AmplifyAPIWrapper.Put<ICrewsNonFlyingAssignment>(
        `/nfas/updateNFA/${nonFlyingAssignment._id}`,
        nonFlyingAssignment,
        notifications
      );
      if (response?._id) setNFA(response);
    } else {
      // ADD NEW
      // const selectedTimes = [nonFlyingAssignment.start, nonFlyingAssignment.end];
      const body = {
        crewId: nonFlyingAssignment?.crew[0]._id,
        nonFlyingTypeId: nonFlyingAssignment?.assignmentType?._id,
        selectedTimes: selectedCrewDays?.selectedTimes,
      };
      const response = await AmplifyAPIWrapper.Post<ICrewsNonFlyingAssignment>("/nfas", body, notifications);
      if (response?._id) setNFA(response);
    }
  };

  return (
    <OCNFAEditorWindow ref={containerRef}>
      <SpinnerProgress loading={!nfa || loading} message="loading non-flying assignment" fullscreen={false} />

      {!nfa?._id && !selectedCrewDays ? (
        <Grid item className="form_grid">
          To add a new non-flying assignment please select one or more days for a specified crew member.
        </Grid>
      ) : nfa?._id && !nfaExistsInRedux ? (
        <Grid item className="form_grid">
          The non-flying assignment you were editing was not found in the crew schedule data. Please reload the crew
          schedule display and attempt to edit again
        </Grid>
      ) : (
        <Grid item className="form_grid">
          {!nfa?._id && selectedCrewDays?.selectedTimes?.length > 0 && (
            <Typography>
              New non-flying assignments will be created for each of the following days.
              {selectedCrewDays?.selectedTimes.map((time, index) => (
                <span key={index}>
                  {" "}
                  <DateDisplay date={time} />,
                </span>
              ))}
            </Typography>
          )}

          <NonFlyingAssignmentForm
            isNew={!nfa?._id}
            mosaicPane={null}
            nonFlyingAssignment={nfa}
            updateNonFlyingAssignment={(nonFlyingAssignment: ICrewsNonFlyingAssignment) => {
              handleSubmit(nonFlyingAssignment);
            }}
            crewOptions={groupedCrewList}
          />
        </Grid>
      )}
    </OCNFAEditorWindow>
  );
};
